import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import get from 'lodash/get';
import { bugsnagClient } from 'ErrorBoundary';
import { useClearImpersonationData } from 'hooks/useClearImpersonationData';
import Alert from 'components/Base/Alert';
import { regexPatterns } from 'components/Base/Form';
import LoginButtonGroups, { EVia } from 'components/Shared/LoginButtonGroups';
import useResendTenantInvitation from 'hooks/useResendTenantInvitation';
import { useGetTenantProfileQuery } from 'redux/apiSlice/tenant.slices';
import { useSignInWithEmailAmplifyMutation } from 'redux/apiSlice/authAmplifyWithEmail.slice';
import { useSignInWithPhoneNumberAmplifyMutation } from 'redux/apiSlice/authAmplifyWithPhoneNumber.slice';
import { RoutesPaths } from 'routes/Routes.types';
import {
  EErrorCode,
  LOGIN_FAILURE_MESSAGE,
  TAmplifyErrorPayload,
} from 'data/types/amplify.types';
import { NotifiableError } from '@bugsnag/js';
import LoginEmailForm from '../LoginEmailForm';
import LoginPhoneNumberForm from '../LoginPhoneNumberForm';
import {
  ELoginErrors,
  TLoginEmailData,
  TLoginPhoneNumberData,
} from '../../Login.types';

const LoginFlow = () => {
  const navigate = useNavigate();
  const removeImpersonationData = useClearImpersonationData();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const passCode = searchParams.get('pass');
  const [via, setVia] = useState<EVia | null>(null);
  const [
    logInWithEmail,
    {
      isSuccess: emailSuccess,
      data: emailUser,
      isLoading: emailLoading,
      error: emailError,
    },
  ] = useSignInWithEmailAmplifyMutation();
  const [
    logInWithPhone,
    {
      isSuccess: phoneSuccess,
      data: phoneUser,
      isLoading: phoneLoading,
      error: phoneError,
    },
  ] = useSignInWithPhoneNumberAmplifyMutation();
  const { isLoading: tenantLoading } = useGetTenantProfileQuery(null, {
    refetchOnMountOrArgChange: true,
    skip:
      (!emailSuccess && !phoneSuccess) ||
      [emailUser?.challengeName, phoneUser?.challengeName].includes(
        'NEW_PASSWORD_REQUIRED',
      ),
  });

  const {
    resendTenantInvitation,
    isError: isResendError,
    isLoading: resendLoading,
  } = useResendTenantInvitation(() => setVia(null));

  const handleNewPasswordRequired = (
    values: TLoginEmailData | TLoginPhoneNumberData,
  ) => {
    searchParams.delete('username');
    searchParams.delete('pass');
    navigate(
      {
        pathname: `/${RoutesPaths.NEW_PASSWORD}`,
        search: searchParams.toString(),
      },
      {
        state: {
          username:
            (values as TLoginEmailData).email ||
            (values as TLoginPhoneNumberData).phoneNumber,
          temporaryPassword: values.password,
        },
      },
    );
  };

  const handleError = (error: TAmplifyErrorPayload, name: string) => {
    const code = get(error, 'data.code');
    const message = get(error, 'data.message');
    if (
      code === EErrorCode.NOT_AUTHORIZED_EXCEPTION &&
      message?.includes('expired')
    ) {
      resendTenantInvitation(
        name,
        `Temporary password has expired. New password has been sent to ${name}.`,
      );
    }
  };

  const onEmailSubmit = async (values: TLoginEmailData) => {
    try {
      removeImpersonationData();
      const data = await logInWithEmail(values).unwrap();
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        handleNewPasswordRequired(values);
      }
    } catch (error) {
      bugsnagClient?.notify(error as NotifiableError);
      bugsnagClient?.addMetadata('metadata', { username: values.email });
      handleError(error as TAmplifyErrorPayload, values.email);
    }
  };

  const onPhoneSubmit = async (values: TLoginPhoneNumberData) => {
    try {
      removeImpersonationData();
      const data = await logInWithPhone(values).unwrap();
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        handleNewPasswordRequired(values);
      }
    } catch (error) {
      bugsnagClient?.notify(error as NotifiableError);
      bugsnagClient?.addMetadata('metadata', { username: values.phoneNumber });
      handleError(error as TAmplifyErrorPayload, `+${values.phoneNumber}`);
    }
  };

  useEffect(() => {
    if (username) {
      const isEmail = regexPatterns.email.test(username);
      const isPhone = regexPatterns.phoneNumber.test(username);
      if (isEmail) {
        setVia(EVia.EMAIL);
      }
      if (isPhone) {
        setVia(EVia.PHONE);
      }
    }
  }, [username]);

  const renderBody = () => {
    switch (via) {
      case EVia.EMAIL:
        return (
          <LoginEmailForm
            onViaChanged={() => setVia(EVia.PHONE)}
            onSubmit={onEmailSubmit}
            userCredential={{
              email: username,
              password: passCode,
            }}
            isLoading={emailLoading || tenantLoading || resendLoading}
          />
        );
      case EVia.PHONE:
        return (
          <LoginPhoneNumberForm
            onViaChanged={() => setVia(EVia.EMAIL)}
            onSubmit={onPhoneSubmit}
            userCredential={{
              phoneNumber: username ? username.slice(1) : null,
              password: passCode,
            }}
            isLoading={phoneLoading || tenantLoading || resendLoading}
          />
        );
      default:
        return <LoginButtonGroups mode="LOGIN" onClick={setVia} />;
    }
  };

  useEffect(() => {
    if (via !== null) {
      localStorage.setItem('viaEmail', JSON.stringify(via === EVia.EMAIL));
    }
  }, [via]);

  const { message, code } =
    ((emailError || phoneError) as TAmplifyErrorPayload)?.data || {};

  return (
    <>
      <Alert
        type="error"
        message={
          code === 'NotAuthorizedException' && message?.includes('expired')
            ? isResendError &&
              (ELoginErrors[code as keyof typeof ELoginErrors] || message)
            : LOGIN_FAILURE_MESSAGE[
                message as keyof typeof LOGIN_FAILURE_MESSAGE
              ] || message
        }
      />
      {renderBody()}
    </>
  );
};

export default LoginFlow;
